import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { userEndpoints } from '../../services/api/endpoints/user.endpoint';
import { CreateUserDTO, UpdateUserDTO } from '../../types/user';
import { logger } from '../../utils/logger';

export const useUserActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createUser = async (data: CreateUserDTO) => {
    if (!selectedOrganizationId && !data.orgId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Backend handles Cognito user creation (AdminCreateUser) and sends activation email
      const result = await userEndpoints.createUser(selectedOrganizationId ?? data?.orgId, data);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create user';
      setError(message);
      logger.error('Error creating user:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = async (cognitoId: string, data: UpdateUserDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await userEndpoints.updateUser(selectedOrganizationId, cognitoId, data);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update user';
      setError(message);
      logger.error('Error updating user:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteUser = async (cognitoId: string) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      // const cognitoResult = await CognitoService.deleteUser(cognitoId);
      await userEndpoints.deleteUser(selectedOrganizationId, cognitoId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete user';
      setError(message);
      logger.error('Error deleting user:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createUser,
    updateUser,
    deleteUser,
    isLoading,
    error,
  };
};
